import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Calendar, Users, Trash2, Loader2, Plane } from 'lucide-react';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import MemberCenterLayout from '../components/common/MemberCenterLayout';

const typeConfig = {
    event_request: { icon: Calendar, color: '#4e8bca', bg: '#eef4f9' },
    event_approved: { icon: Check, color: '#10b981', bg: '#d1fae5' },
    event_rejected: { icon: Calendar, color: '#ef4444', bg: '#fee2e2' },
    friend_accepted: { icon: Users, color: '#4e8bca', bg: '#eef4f9' },
    travel_application: { icon: Plane, color: '#4e8bca', bg: '#eef4f9' },
    travel_application_accepted: { icon: Plane, color: '#10b981', bg: '#d1fae5' },
    travel_application_rejected: { icon: Plane, color: '#ef4444', bg: '#fee2e2' },
};

export default function Notifications() {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const isFirstLoad = useRef(true);

    const loadNotifications = useCallback(async () => {
        if (!currentUser) return;
        if (isFirstLoad.current) setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', currentUser.id)
                .order('created_at', { ascending: false })
                .limit(50);
            if (error) throw error;
            setNotifications(data || []);
        } catch (err) {
            console.error('載入通知失敗：', err);
        } finally {
            setIsLoading(false);
            isFirstLoad.current = false;
        }
    }, [currentUser]);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    // Realtime：即時接收新通知
    useEffect(() => {
        if (!currentUser?.id) return;

        const channel = supabase
            .channel('realtime-notifications')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${currentUser.id}`
            }, (payload) => {
                setNotifications(prev => [payload.new, ...prev]);
            })
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [currentUser]);

    const markAsRead = async (id) => {
        await supabase.from('notifications').update({ is_read: true }).eq('id', id);
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    };

    const markAllRead = async () => {
        await supabase.from('notifications').update({ is_read: true }).eq('user_id', currentUser.id);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    };

    const deleteNotification = async (id) => {
        await supabase.from('notifications').delete().eq('id', id);
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const handleNotificationClick = async (notif) => {
        if (!notif.is_read) await markAsRead(notif.id);
        if (notif.related_event_id) navigate('/events');
        else if (notif.related_user_id) navigate(`/user/${notif.related_user_id}`);
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <MemberCenterLayout
            title="通知中心"
            subtitle={unreadCount > 0 ? `目前有 ${unreadCount} 則未讀通知` : '好友邀請與活動狀態會顯示在這裡'}
            activeKey="notifications"
            headerAction={unreadCount > 0 ? <button type="button" onClick={markAllRead} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minHeight: '40px', padding: '8px 12px', color: '#376ba1', background: '#f6f9fc', border: '1px solid #dbe4ee', borderRadius: '12px', fontWeight: '750', cursor: 'pointer' }}><CheckCheck size={16} /> 全部已讀</button> : null}
        >
            <div style={{ maxWidth: '780px', margin: '0 auto' }}>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <Loader2 size={32} color="#4e8bca" style={{ animation: 'spin 1s linear infinite' }} />
                        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
                        <Bell size={48} strokeWidth={1.5} style={{ marginBottom: '16px', opacity: 0.4 }} />
                        <p style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px' }}>目前沒有通知</p>
                        <p style={{ fontSize: '14px' }}>好友邀請和活動審核通知會出現在這裡</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {notifications.map((notif) => {
                            const config = typeConfig[notif.type] || typeConfig.event_approved;
                            const Icon = config.icon;
                            return (
                                <div
                                    key={notif.id}
                                    onClick={() => handleNotificationClick(notif)}
                                    style={{
                                        background: notif.is_read ? 'white' : '#f0faf8',
                                        borderRadius: '14px',
                                        padding: '14px 16px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        cursor: 'pointer',
                                        border: `1px solid ${notif.is_read ? '#e5e7eb' : '#b2dfdb'}`,
                                        transition: 'all 0.2s ease',
                                        boxShadow: notif.is_read ? 'none' : '0 2px 8px rgba(78,139,202,0.08)',
                                    }}
                                >
                                    {/* Icon */}
                                    <div style={{
                                        width: '44px', height: '44px', borderRadius: '12px',
                                        background: config.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                    }}>
                                        <Icon size={22} color={config.color} />
                                    </div>
                                    {/* Text */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{
                                            margin: '0 0 3px', fontSize: '15px', fontWeight: notif.is_read ? '500' : '700', color: '#1a1a1a'
                                        }}>{notif.title}</p>
                                        <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>{notif.body}</p>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                                            {new Date(notif.created_at).toLocaleDateString('zh-TW', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                    {/* Unread dot + delete */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        {!notif.is_read && (
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4e8bca' }} />
                                        )}
                                        <button
                                            type="button"
                                            aria-label="刪除通知"
                                            onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#d1d5db' }}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </MemberCenterLayout>
    );
}
